import logger from '@/utils/client-logger';
import { ReportingCenter } from './reportingCenter';
import { Provider, ProviderType, TrackingEvent } from './types';
import { generateEventId } from './utils/eventIdGenerator';

/** 第三方登录身份同步完成后所需的关键事件输入。 */
export interface ThirdPartyLoginCriticalEventInput {
  uuid: string;
  snowplowUserId?: string;
  properties: Record<string, unknown>;
}

/** 协调器发送事件所需的最小 Provider 契约。 */
type CriticalTrackingProvider = Pick<Provider, 'type' | 'isEnabled' | 'track'>;

/** Sensors Provider 在最小发送契约之外提供的身份 callback 屏障。 */
interface SensorsLoginProvider extends CriticalTrackingProvider {
  sensorsLogin: (uuid: string, snowplowUserId?: string) => Promise<void>;
}

/** 协调器可注入的 Provider 查询与事件 ID 生成依赖。 */
export interface ThirdPartyLoginCoordinatorDeps {
  getProvider: (type: ProviderType) => CriticalTrackingProvider | null | undefined;
  generateEventId: () => string;
}

/** 判断目标 Provider 是否提供 Sensors 登录 callback 屏障。 */
function isSensorsLoginProvider(
  provider: CriticalTrackingProvider | null | undefined,
): provider is SensorsLoginProvider {
  return Boolean(
    provider &&
      provider.type === ProviderType.SENSORS &&
      'sensorsLogin' in provider &&
      typeof provider.sensorsLogin === 'function',
  );
}

/** 等待 Sensors 身份 callback 后，向两个关键 Provider 发送同一登录事件。 */
export async function reportThirdPartyLoginSuccessAfterIdentity(
  input: ThirdPartyLoginCriticalEventInput,
  deps: ThirdPartyLoginCoordinatorDeps = {
    getProvider: (type) => ReportingCenter.getInstance().getProvider(type),
    generateEventId,
  },
): Promise<void> {
  const sensors = deps.getProvider(ProviderType.SENSORS);
  if (!isSensorsLoginProvider(sensors) || !sensors.isEnabled) return;

  await sensors.sensorsLogin(input.uuid, input.snowplowUserId);
  const event: TrackingEvent = {
    eventName: 'LoginSuccess_Result',
    properties: input.properties,
    eventId: deps.generateEventId(),
  };
  sensors.track(event);

  const snowplow = deps.getProvider(ProviderType.SNOWPLOW);
  if (!snowplow?.isEnabled) return;

  // Snowplow 单点失败只记录日志，不能撤销或重发已完成的 Sensors 事件。
  try {
    snowplow.track(event);
  } catch (error) {
    logger.error('Third party login Snowplow tracking failed:', error);
  }
}
